/**
 * Zustand store for managing call state across the PeersCaller library
 */

import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type {
  CallState,
  CallParticipant,
  PeersCallerError,
  CallStatusResponse,
} from "../types";

interface CallStoreActions {
  // Conversation management
  setConversationId: (conversationId: string) => void;

  // Participant management
  setLocalParticipant: (participant: CallParticipant) => void;
  addParticipant: (participant: CallParticipant) => void;
  removeParticipant: (userId: string) => void;
  updateParticipant: (
    userId: string,
    updates: Partial<CallParticipant>
  ) => void;
  clearParticipants: () => void;

  // Call state management
  setCallStatus: (status: CallState["callStatus"]) => void;
  setIsCalling: (isCalling: boolean) => void;

  // Recording management
  setIsRecording: (isRecording: boolean) => void;
  setRecordLoading: (loading: boolean) => void;

  // Error management
  setError: (error: PeersCallerError | null, message?: string) => void;
  clearError: () => void;

  // Call status management
  setCallStatusInfo: (statusInfo: CallStatusResponse | null) => void;
  setCallStatusLoading: (loading: boolean) => void;
  setCallStatusError: (error: string | null) => void;

  // Reset store
  resetCall: () => void;
}

export type CallStore = CallState & CallStoreActions;

const initialState: CallState = {
  conversationId: "",
  participants: {},
  localParticipant: null,
  isCalling: false,
  isRecording: false,
  recordLoading: false,
  callStatus: "idle",
  error: null,
  callStatusInfo: null,
  callStatusLoading: false,
  callStatusError: null,
};

export const useCallStore = create<CallStore>()(
  devtools(
    (set) => ({
      ...initialState,

      // Conversation management
      setConversationId: (conversationId) =>
        set({ conversationId }, false, "setConversationId"),

      // Participant management
      setLocalParticipant: (participant) =>
        set({ localParticipant: participant }, false, "setLocalParticipant"),

      addParticipant: (participant) =>
        set(
          (state) => ({
            participants: {
              ...state.participants,
              [participant.userId]: participant,
            },
          }),
          false,
          "addParticipant"
        ),

      removeParticipant: (userId) =>
        set(
          (state) => {
            const { [userId]: removed, ...remaining } = state.participants;
            return { participants: remaining };
          },
          false,
          "removeParticipant"
        ),

      updateParticipant: (userId, updates) =>
        set(
          (state) => ({
            participants: {
              ...state.participants,
              [userId]: state.participants[userId]
                ? { ...state.participants[userId], ...updates }
                : {
                    userId,
                    videoOn: true,
                    audioOn: true,
                    screenSharing: false,
                    ...updates,
                  },
            },
          }),
          false,
          "updateParticipant"
        ),

      clearParticipants: () =>
        set({ participants: {} }, false, "clearParticipants"),

      // Call state management
      setCallStatus: (callStatus) =>
        set({ callStatus }, false, "setCallStatus"),

      setIsCalling: (isCalling) => set({ isCalling }, false, "setIsCalling"),

      // Recording management
      setIsRecording: (isRecording) =>
        set({ isRecording }, false, "setIsRecording"),

      setRecordLoading: (recordLoading) =>
        set({ recordLoading }, false, "setRecordLoading"),

      // Error management
      setError: (error, message) =>
        set({ error: message || error }, false, "setError"),

      clearError: () => set({ error: null }, false, "clearError"),

      // Call status management
      setCallStatusInfo: (callStatusInfo) =>
        set({ callStatusInfo }, false, "setCallStatusInfo"),

      setCallStatusLoading: (callStatusLoading) =>
        set({ callStatusLoading }, false, "setCallStatusLoading"),

      setCallStatusError: (callStatusError) =>
        set({ callStatusError }, false, "setCallStatusError"),

      // Reset store
      resetCall: () => set({ ...initialState }, false, "resetCall"),
    }),
    {
      name: "peers-caller-store",
      enabled: process.env.NODE_ENV === "development",
    }
  )
);

// Selectors for common state queries
export const selectParticipants = (state: CallStore) => state.participants;
export const selectParticipantCount = (state: CallStore) =>
  Object.keys(state.participants).length;
export const selectLocalParticipant = (state: CallStore) =>
  state.localParticipant;
export const selectCallStatus = (state: CallStore) => state.callStatus;
export const selectIsConnected = (state: CallStore) =>
  state.callStatus === "connected";
export const selectError = (state: CallStore) => state.error;

// Call status selectors
export const selectCallStatusInfo = (state: CallStore) => state.callStatusInfo;
export const selectCallStatusLoading = (state: CallStore) =>
  state.callStatusLoading;
export const selectCallStatusError = (state: CallStore) =>
  state.callStatusError;
export const selectCanJoinCall = (state: CallStore) =>
  state.callStatusInfo?.canJoin || false;
export const selectIsCallActive = (state: CallStore) =>
  state.callStatusInfo?.hasActiveCall || false;

// Utility functions for store interactions
export const getParticipant = (userId: string) => {
  const state = useCallStore.getState();
  return state.participants[userId];
};

export const isParticipantConnected = (userId: string) => {
  const participant = getParticipant(userId);
  return !!participant?.stream;
};

export const getParticipantList = () => {
  const state = useCallStore.getState();
  return Object.values(state.participants);
};

export const canAcceptMoreParticipants = (maxParticipants = 4) => {
  const count = selectParticipantCount(useCallStore.getState());
  return count < maxParticipants;
};
